(() => {
  const REWARD_GIFT_FLAG = '_reward_gift';

  let cleanupRunning = false;

  /**
   * Fetch the latest Shopify cart.
   */
  async function getCart() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.warn(
        '[empty-cart-gift] Failed to fetch cart:',
        error
      );

      return null;
    }
  }

  /**
   * Remove reward gifts.
   */
  async function removeRewardGifts(cart) {
    if (!cart?.items?.length) {
      return false;
    }

    /**
     * ONLY run when cart total is zero.
     */
    if (Number(cart.total_price || 0) !== 0) {
      return false;
    }

    const gifts = cart.items.filter(
      (item) =>
        item?.properties?.[REWARD_GIFT_FLAG] === 'true'
    );

    if (!gifts.length) {
      return false;
    }

    console.log(
      '[empty-cart-gift] Cart total is 0. Removing reward gift.'
    );

    let removed = false;

    for (const gift of gifts) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            id: gift.key,
            quantity: 0,
          }),
        });

        if (!response.ok) {
          console.warn(
            '[empty-cart-gift] Failed to remove gift:',
            gift.key
          );

          continue;
        }

        removed = true;

        console.log(
          '[empty-cart-gift] Gift removed:',
          gift.key
        );
      } catch (error) {
        console.warn(
          '[empty-cart-gift] Gift removal failed:',
          error
        );
      }
    }

    /**
     * IMPORTANT:
     *
     * Horizon is not updating its visible cart after our
     * direct /cart/change.js request.
     *
     * Therefore reload the current page after the gift
     * has actually been removed.
     *
     * This will cause Shopify/Horizon to render the real
     * empty cart state.
     */
    if (removed) {
      console.log(
        '[empty-cart-gift] Refreshing page to render empty cart.'
      );

      window.location.reload();
    }

    return removed;
  }

  /**
   * Check whether cart is empty of actual products.
   */
  async function checkEmptyCart() {
    if (cleanupRunning) {
      return;
    }

    cleanupRunning = true;

    try {
      /**
       * Give Horizon time to finish its own cart operation.
       */
      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });

      const cart = await getCart();

      if (!cart) {
        return;
      }

      /**
       * Only act when cart total is exactly zero.
       */
      if (Number(cart.total_price || 0) !== 0) {
        return;
      }

      await removeRewardGifts(cart);
    } finally {
      cleanupRunning = false;
    }
  }

  /**
   * ---------------------------------------------------------
   * HORIZON CART EVENTS
   * ---------------------------------------------------------
   */

  document.addEventListener(
    'cart:update',
    () => {
      checkEmptyCart();
    }
  );

  document.addEventListener(
    'cart:line-update',
    () => {
      checkEmptyCart();
    }
  );

  document.addEventListener(
    'cart-lines-update',
    () => {
      checkEmptyCart();
    }
  );

  /**
   * ---------------------------------------------------------
   * INITIAL CHECK
   * ---------------------------------------------------------
   */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        checkEmptyCart();
      },
      { once: true }
    );
  } else {
    checkEmptyCart();
  }

  /**
   * ---------------------------------------------------------
   * FALLBACK CART WATCHER
   * ---------------------------------------------------------
   */

  let lastCartSignature = '';

  async function pollCart() {
    if (cleanupRunning) {
      return;
    }

    const cart = await getCart();

    if (!cart) {
      return;
    }

    const signature = [
      cart.item_count,
      cart.total_price,
      (cart.items || [])
        .map(
          (item) =>
            `${item.key}:${item.quantity}`
        )
        .join('|'),
    ].join('::');

    if (
      lastCartSignature &&
      signature !== lastCartSignature
    ) {
      await removeRewardGifts(cart);
    }

    lastCartSignature = signature;
  }

  setInterval(
    pollCart,
    100
  );
})();

